import { useCallback, useEffect, useState } from 'react';
import { kurzlinksAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Link2, Plus, Copy, Check, QrCode, Trash2, Loader2, RefreshCw, Eye, Pencil,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

/* Kurzlinks — /r/<code> für Flyer, QR-Codes und Aushänge.
 *
 * Warum das nicht einfach ein UTM-Link ist: Auf einem gedruckten Flyer steht
 * der Link für immer. Ein Kurzlink lässt sich später auf ein anderes Ziel
 * umbiegen, ist im QR-Code robuster (weniger Daten = größere Punkte = liest
 * sich auch zerknittert), und man kann ihn abtippen.
 *
 * Der QR kommt als SVG vom Server: Ein Flyer wird in jeder Größe gedruckt,
 * und ein hochskaliertes Pixelbild wird unscharf — ein unscharfer QR-Code
 * wird nicht gelesen.
 */

/* Muss zu herkunft.KANAELE im Backend passen. */
const KANAELE = [
  { wert: 'flyer', label: 'Flyer' },
  { wert: 'aushang', label: 'Aushang / QR-Plakat' },
  { wert: 'meta', label: 'Meta (Facebook)' },
  { wert: 'instagram', label: 'Instagram' },
  { wert: 'google', label: 'Google' },
  { wert: 'whatsapp', label: 'WhatsApp' },
  { wert: 'whatsapp_community', label: 'WhatsApp-Community (Mitglieder)' },
  { wert: 'empfehlung', label: 'Empfehlung' },
  { wert: 'laufkundschaft', label: 'Laufkundschaft' },
  { wert: 'direkt', label: 'Direkt' },
];

/* Muss zu ERLAUBTE_ZIELE im Backend passen. */
const ZIELE = [
  { wert: '/funnel', label: 'Probetraining Erwachsene (/funnel)' },
  { wert: '/funnelkids', label: 'Probetraining Kinder (/funnelkids)' },
  { wert: '/frauen', label: 'Frauen (/frauen)' },
  { wert: '/seminar', label: 'Seminaranmeldung (/seminar)' },
  { wert: '/ptl', label: 'Personal Training Lounge (/ptl)' },
  { wert: '/zinzino', label: 'Zinzino (/zinzino)' },
];

const LEER = { code: '', ziel: '/funnel', kanal: 'flyer', kampagne: '', beschreibung: '' };

export default function KurzlinksPage() {
  const [links, setLinks] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [formular, setFormular] = useState(LEER);
  const [sendet, setSendet] = useState(false);
  const [kopiert, setKopiert] = useState(null);
  const [busy, setBusy] = useState(null);
  const [bearbeite, setBearbeite] = useState(null);  // code -> neues Ziel

  const basis = `${window.location.origin}/r/`;

  const laden = useCallback(async () => {
    try {
      const { data } = await kurzlinksAPI.liste();
      setLinks(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Kurzlinks konnten nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const anlegen = async (e) => {
    e.preventDefault();
    const code = formular.code.trim().toLowerCase();
    if (!/^[a-z0-9-]{2,40}$/.test(code)) {
      toast.error('Der Code darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten');
      return;
    }
    setSendet(true);
    try {
      await kurzlinksAPI.anlegen({ ...formular, code });
      setFormular(LEER);
      toast.success('Kurzlink angelegt');
      laden();
    } catch (err) {
      toast.error(err?.response?.status === 409
        ? 'Diesen Code gibt es schon'
        : err?.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally {
      setSendet(false);
    }
  };

  const kopieren = async (code) => {
    try {
      await navigator.clipboard.writeText(basis + code);
      setKopiert(code);
      setTimeout(() => setKopiert((k) => (k === code ? null : k)), 2000);
    } catch {
      toast.error('Kopieren nicht möglich — Adresse bitte markieren');
    }
  };

  const qrHolen = async (code) => {
    setBusy(code);
    try {
      const { data } = await kurzlinksAPI.qr(code);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${code}.svg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('QR-Code konnte nicht erzeugt werden');
    } finally {
      setBusy(null);
    }
  };

  const zielAendern = async (code, ziel) => {
    setBusy(code);
    try {
      await kurzlinksAPI.aendern(code, { ziel });
      setLinks((l) => l.map((x) => (x.code === code ? { ...x, ziel } : x)));
      setBearbeite(null);
      toast.success('Ziel umgebogen — der gedruckte Code bleibt gültig');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ändern fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  };

  const loeschen = async (code) => {
    setBusy(code);
    try {
      await kurzlinksAPI.loeschen(code);
      setLinks((l) => l.filter((x) => x.code !== code));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Link2 className="w-6 h-6 text-primary" /> Kurzlinks
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Kurze Adressen mit QR-Code für Flyer, Aushänge und Anzeigen — die
            Herkunft hängt danach automatisch am Lead.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={laden}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Aktualisieren
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={anlegen} className="grid gap-3 md:grid-cols-2">
            <div>
              <Label htmlFor="kl-code">Code *</Label>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-sm text-muted-foreground whitespace-nowrap">{basis}</span>
                <Input id="kl-code" value={formular.code} placeholder="schulstart"
                       onChange={(e) => setFormular({ ...formular, code: e.target.value })} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Kleinbuchstaben, Ziffern, Bindestriche. Kurz halten — je weniger
                Zeichen, desto größer die Punkte im QR-Code.
              </p>
            </div>

            <div>
              <Label htmlFor="kl-ziel">Wohin führt der Link? *</Label>
              <select id="kl-ziel" value={formular.ziel}
                      onChange={(e) => setFormular({ ...formular, ziel: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                {ZIELE.map((z) => <option key={z.wert} value={z.wert}>{z.label}</option>)}
              </select>
            </div>

            <div>
              <Label htmlFor="kl-kanal">Kanal *</Label>
              <select id="kl-kanal" value={formular.kanal}
                      onChange={(e) => setFormular({ ...formular, kanal: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                {KANAELE.map((k) => <option key={k.wert} value={k.wert}>{k.label}</option>)}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Landet als Herkunft am Lead — danach ist auswertbar, was der
                Kanal gebracht hat.
              </p>
            </div>

            <div>
              <Label htmlFor="kl-kampagne">Kampagne</Label>
              <Input id="kl-kampagne" value={formular.kampagne} placeholder="schulstart-2026"
                     className="mt-1"
                     onChange={(e) => setFormular({ ...formular, kampagne: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1">
                Trennt mehrere Aktionen im selben Kanal voneinander.
              </p>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="kl-beschreibung">Notiz für euch</Label>
              <Input id="kl-beschreibung" value={formular.beschreibung} className="mt-1"
                     placeholder="2.000 Flyer, Verteilung Grundschulen Eutingen, ab 14.09."
                     onChange={(e) => setFormular({ ...formular, beschreibung: e.target.value })} />
            </div>

            <div className="md:col-span-2">
              <Button type="submit" disabled={sendet || !formular.code.trim()}>
                {sendet ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        : <Plus className="w-4 h-4 mr-1.5" />}
                Kurzlink anlegen
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {laedt ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : links.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Link2 className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-foreground">Noch kein Kurzlink</p>
            <p className="text-sm mt-1">Leg oben den ersten an.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {links.map((l) => (
            <Card key={l.code}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-mono text-sm font-medium break-all">
                      {basis}{l.code}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                      {bearbeite?.code === l.code ? (
                        <select autoFocus value={bearbeite.ziel}
                                onChange={(e) => setBearbeite({ code: l.code, ziel: e.target.value })}
                                className="h-8 rounded-md border border-input bg-background px-2 text-xs">
                          {ZIELE.map((z) => <option key={z.wert} value={z.wert}>{z.label}</option>)}
                        </select>
                      ) : (
                        <span>&rarr; {l.ziel}</span>
                      )}
                      <Badge variant="secondary">{
                        KANAELE.find((k) => k.wert === l.kanal)?.label || l.kanal
                      }</Badge>
                      {l.kampagne && <Badge variant="outline">{l.kampagne}</Badge>}
                    </div>
                    {l.beschreibung && (
                      <p className="text-xs text-muted-foreground mt-1">{l.beschreibung}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                    <Eye className="w-4 h-4" />
                    {l.aufrufe || 0}&nbsp;{(l.aufrufe === 1) ? 'Aufruf' : 'Aufrufe'}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => kopieren(l.code)}>
                    {kopiert === l.code
                      ? <><Check className="w-4 h-4 mr-1.5 text-emerald-600" /> Kopiert</>
                      : <><Copy className="w-4 h-4 mr-1.5" /> Adresse kopieren</>}
                  </Button>
                  {bearbeite?.code === l.code ? (
                    <>
                      <Button size="sm" disabled={busy === l.code}
                              onClick={() => zielAendern(l.code, bearbeite.ziel)}>
                        {busy === l.code
                          ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          : <Check className="w-4 h-4 mr-1.5" />}
                        Ziel speichern
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setBearbeite(null)}>
                        Abbrechen
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline"
                            onClick={() => setBearbeite({ code: l.code, ziel: l.ziel })}>
                      <Pencil className="w-4 h-4 mr-1.5" /> Ziel umbiegen
                    </Button>
                  )}
                  <Button size="sm" variant="outline" disabled={busy === l.code}
                          onClick={() => qrHolen(l.code)}>
                    {busy === l.code
                      ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      : <QrCode className="w-4 h-4 mr-1.5" />}
                    QR-Code (SVG)
                  </Button>
                  {(l.aufrufe || 0) === 0 && (
                    <Button size="sm" variant="ghost" disabled={busy === l.code}
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => loeschen(l.code)}>
                      <Trash2 className="w-4 h-4 mr-1.5" /> Löschen
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Der Code steht auf Papier und bleibt deshalb fest — das Ziel kannst du
        jederzeit umbiegen, ohne dass gedruckte Flyer wertlos werden. Ein
        Kurzlink, der schon aufgerufen wurde, lässt sich nicht mehr löschen.
      </p>
    </div>
  );
}
